/**
 * WelcomePricingStep — pricing & trial activation for the welcome page.
 *
 * Structure:
 * 1. How it works (Free Reading + Assessments & Lessons)
 * 2. How different families use Turn (3-tier grid)
 * 3. Trial Activation (checkboxes + CTA)
 */

import React, { useRef } from 'react';
import { Loader2, CreditCard } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  formatCurrency,
  PRICING,
  FAMILY_FAVORITES,
} from './welcomePricing';

interface WelcomePricingStepProps {
  childName: string;
  formattedTrialEnd: string;
  isExistingSubscriber: boolean;
  setIsExistingSubscriber: (v: boolean) => void;
  agreedToTerms: boolean;
  setAgreedToTerms: (v: boolean) => void;
  startingTrial: boolean;
  handleStartTrial: () => void;
  error: string | null;
}

const ACCENT = '#5C2D91';

export const WelcomePricingStep: React.FC<WelcomePricingStepProps> = ({
  childName,
  formattedTrialEnd,
  isExistingSubscriber,
  setIsExistingSubscriber,
  agreedToTerms,
  setAgreedToTerms,
  startingTrial,
  handleStartTrial,
  error,
}) => {
  const { t } = useTranslation();
  const activationRef = useRef<HTMLDivElement>(null);

  return (
    <div className="space-y-10">

      {/* Intro */}
      <p className="text-base text-[#636366] text-center max-w-lg mx-auto leading-relaxed">
        {t('Turn Method is built around your family\'s pace. Below you\'ll find how our program works and what families like yours typically spend.')}
      </p>

      {/* ─── Section 1: How it works ─── */}
      <section>
        <p className="text-xs font-medium uppercase tracking-[0.15em] text-[#8E8E93] mb-5">
          {t('How it works')}
        </p>

        {/* Free Reading */}
        <div className="rounded-xl bg-white border border-black/[0.08] p-6 mb-3">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-[#1C1C1E]" />
            <span className="font-serif font-medium text-xl text-[#1C1C1E]">{t('Free Reading')}</span>
            <span className="font-serif italic text-base text-[#8E8E93] ml-auto">{t('always free')}</span>
          </div>
          <p className="text-[15px] leading-relaxed text-[#636366]">
            {t('Reading on our platform with our proprietary leveled reading tool is free, always. Turn Pages lets your child explore their favorite books through adaptive, scaffolded reading targets \u2014 the most important need your child has as an emerging reader. We encourage families to read at home with Turn Pages as much as possible.')}
          </p>
        </div>

        {/* Assessments & Lessons */}
        <div className="rounded-xl bg-white border border-black/[0.08] p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ACCENT }} />
            <span className="font-serif font-medium text-xl text-[#1C1C1E]">{t('Assessments & Lessons')}</span>
            <span className="font-serif italic text-base text-[#8E8E93] ml-auto">{t('usage-based')}</span>
          </div>
          <p className="text-[15px] leading-relaxed text-[#636366] mb-5">
            {t('Your child can develop their skills through a mix of Live and On-Demand lessons.')}
          </p>

          {/* Live */}
          <div className="rounded-lg bg-black/[0.02] p-4 mb-3">
            <div className="flex items-baseline gap-2.5 mb-1.5">
              <span className="font-serif font-semibold text-lg" style={{ color: ACCENT }}>{t('Live')}</span>
              <span className="text-sm text-[#8E8E93]">{formatCurrency(PRICING.LIVE_HOURLY_RATE_CENTS)}/{t('hour')}</span>
            </div>
            <p className="text-[15px] leading-relaxed text-[#636366]">
              {t('25-minute sessions with Addison Hoffman, bookable 12 hours in advance.')}
            </p>
          </div>

          {/* On-Demand */}
          <div className="rounded-lg bg-black/[0.02] p-4 mb-4">
            <div className="flex items-baseline gap-2.5 mb-1.5">
              <span className="font-serif font-semibold text-lg text-[#1C1C1E]">{t('On-Demand')}</span>
              <span className="text-sm text-[#8E8E93]">{formatCurrency(PRICING.ON_DEMAND_HOURLY_RATE_CENTS)}/{t('hour')}</span>
            </div>
            <p className="text-[15px] leading-relaxed text-[#636366]">
              {t('Addison\'s exact methodology, available on our platform 24/7, adapted to the exact module your child needs to complete next.')}
            </p>
          </div>

          <p className="text-sm text-[#8E8E93] italic leading-relaxed">
            {t('Both Live and On-Demand sessions are billed at the end of the month based on actual usage, with no monthly minimums.')}
          </p>
        </div>
      </section>

      {/* ─── Section 2: How different families use Turn ─── */}
      <section>
        <p className="text-xs font-medium uppercase tracking-[0.15em] text-[#8E8E93] mb-5">
          {t('How different families use Turn')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {FAMILY_FAVORITES.map((fav, i) => (
            <div key={i} className="rounded-xl bg-white border border-black/[0.08] p-5">
              <p className="font-serif font-medium text-lg text-[#1C1C1E] mb-2">{fav.name}</p>
              <p className="text-sm text-[#8E8E93] mb-4 leading-relaxed">{fav.description}</p>
              <p className="font-serif font-semibold text-2xl" style={{ color: ACCENT }}>
                ~{formatCurrency(fav.monthlyCents)}<span className="text-sm font-normal text-[#8E8E93]">/mo</span>
              </p>
            </div>
          ))}
        </div>

        <p className="text-sm text-[#8E8E93] text-center mt-4 italic">
          {t('Turn Pages included at no cost for all Turn families')}
        </p>
      </section>

      {/* ─── Section 3: Trial Activation ─── */}
      <section ref={activationRef}>
        <div className="rounded-xl bg-white border border-black/[0.08] p-6">
          <h3 className="font-serif font-medium text-xl text-[#1C1C1E] mb-5">
            {t('Activate')} {childName}{t("'s complimentary trial")}:
          </h3>

          {!isExistingSubscriber && (
            <div className="p-4 bg-black/[0.03] rounded-lg mb-5 flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-[#8E8E93] shrink-0" />
              <p className="text-[15px] text-[#636366]">
                {t('Card information will be collected securely by Stripe')}
              </p>
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={isExistingSubscriber}
              onChange={e => {
                setIsExistingSubscriber(e.target.checked);
                if (e.target.checked) setAgreedToTerms(false);
              }}
              className="mt-1 rounded border-black/[0.12] bg-black/[0.03] text-[#5C2D91] focus:ring-[#5C2D91]/30"
            />
            <span className="text-[15px] text-[#636366]">
              {t('I am a current Turn family with an active subscription.')}
            </span>
          </label>

          {!isExistingSubscriber && (
            <label className="flex items-start gap-3 cursor-pointer mb-6">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                className="mt-1 rounded border-black/[0.12] bg-black/[0.03] text-[#5C2D91] focus:ring-[#5C2D91]/30"
              />
              <span className="text-[15px] text-[#636366] leading-relaxed">
                {t(`I agree that after my ${PRICING.TRIAL_DAYS}-day complimentary trial, I will be billed based on usage on`)} <strong className="text-[#1C1C1E]">{formattedTrialEnd}</strong> {t('unless I cancel before then. No base fee \u2014 only pay for sessions used.')}.
              </span>
            </label>
          )}

          {error && (
            <div className="mb-4 p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-lg text-[#FF3B30] text-[15px]">
              {error}
            </div>
          )}

          <button
            onClick={handleStartTrial}
            disabled={(!agreedToTerms && !isExistingSubscriber) || startingTrial}
            className="w-full px-6 py-4 bg-[#5C2D91] hover:bg-[#4A2476] text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors text-lg disabled:opacity-40"
          >
            {startingTrial ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t(isExistingSubscriber ? 'Creating Account...' : 'Starting Trial...')}
              </>
            ) : (
              t(isExistingSubscriber ? 'Create Account' : 'Start Trial')
            )}
          </button>
        </div>
      </section>
    </div>
  );
};
